import React from 'react';
import { 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Download, 
  FileSearch, 
  ShieldCheck, 
  Upload, 
  Clock,
  X
} from 'lucide-react';
import type { OperationStatus } from '../../types/data-exchange';
import { OPERATION_STATUS_LABELS, OPERATION_STATUS_COLORS } from '../../types/data-exchange';

interface ImportStatusBadgeProps {
  /** Statut courant de l'opération */
  status: OperationStatus;
  /** Progression 0-100 */
  progress: number;
  /** Description courte de l'étape en cours */
  currentStep?: string;
  /** Nombre de lignes traitées */
  processedLines?: number;
  /** Nombre total de lignes */
  totalLines?: number;
  /** Nombre d'erreurs */
  errorCount?: number;
  /** Affichage compact (badge inline) ou étendu (avec barre de progression) */
  compact?: boolean;
  /** Callback pour annuler */
  onCancel?: () => void;
  /** Callback au clic */
  onClick?: () => void;
}

const STATUS_ICONS: Record<OperationStatus, React.ElementType> = {
  pending: Clock,
  downloading: Download,
  processing: Loader2,
  parsing: FileSearch,
  validating: ShieldCheck,
  importing: Upload,
  completed: CheckCircle2,
  failed: XCircle,
  partial: AlertTriangle,
  cancelled: X
};

const SPINNING_STATUSES: OperationStatus[] = ['downloading', 'processing', 'parsing', 'validating', 'importing'];

/**
 * Badge discret affichant le statut et la progression d'une importation de données.
 * Utilise le même pattern visuel que FiscalYearInitStatusBadge.
 * 
 * - Mode compact : badge inline avec icône + pourcentage
 * - Mode étendu  : progression détaillée avec barre et étape
 */
export function ImportStatusBadge({
  status,
  progress,
  currentStep,
  processedLines,
  totalLines,
  errorCount,
  compact = false,
  onCancel,
  onClick
}: ImportStatusBadgeProps) {
  const Icon = STATUS_ICONS[status] || Loader2;
  const colors = OPERATION_STATUS_COLORS[status];
  const label = OPERATION_STATUS_LABELS[status];
  const isActive = SPINNING_STATUSES.includes(status);
  const isTerminal = ['completed', 'failed', 'partial', 'cancelled'].includes(status);

  // ====== MODE COMPACT ======
  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium cursor-default transition-colors ${colors.bg} ${colors.text}`}
        title={currentStep || label}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
      >
        <Icon className={`w-3 h-3 ${isActive ? 'animate-spin' : ''}`} />
        {isActive ? (
          <span>{progress}%</span>
        ) : (
          <span>{label}</span>
        )}
      </span>
    );
  }

  // ====== MODE ÉTENDU ======
  return (
    <div
      className={`rounded-lg border p-3 transition-all ${
        isTerminal
          ? `${colors.bg} border-transparent`
          : 'bg-card border-border'
      }`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      {/* En-tête : icône + label + annuler */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${colors.text} ${isActive ? 'animate-spin' : ''}`} />
          <span className={`text-sm font-medium ${colors.text}`}>{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {isActive && (
            <span className="text-xs text-muted-foreground font-mono">{progress}%</span>
          )}
          {isActive && onCancel && (
            <button
              onClick={(e) => { e.stopPropagation(); onCancel(); }}
              className="text-muted-foreground hover:text-destructive transition-colors p-0.5 rounded"
              title="Annuler l'import"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Barre de progression */}
      {isActive && (
        <div className="w-full bg-muted rounded-full h-1.5 mb-1.5 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ease-out ${colors.dot}`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      )}

      {/* Détails */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        {currentStep && (
          <span className="truncate max-w-[70%]">{currentStep}</span>
        )}
        <div className="flex items-center gap-2 ml-auto">
          {processedLines != null && totalLines != null && totalLines > 0 && (
            <span>{processedLines}/{totalLines} lignes</span>
          )}
          {errorCount != null && errorCount > 0 && (
            <span className="text-red-500 dark:text-red-400">{errorCount} err.</span>
          )}
        </div>
      </div>
    </div>
  );
}
